import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import apiClient, { showcaseAPI } from '@/utils/api';
import { Sparkles, MapPin, Languages, Briefcase, Lock, X, Send, Search, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Footer from '@/components/Footer';
import { useSectors } from '@/hooks/useSectors';

// Deterministic avatar background from seed — generates a nice gradient card
function seedGradient(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffffff;
  const hue1 = h % 360;
  const hue2 = (hue1 + 60) % 360;
  return `linear-gradient(135deg, hsl(${hue1}, 65%, 55%), hsl(${hue2}, 65%, 40%))`;
}

const LANGUAGES = ['Português', 'Crioulo', 'Inglês', 'Francês', 'Espanhol', 'Italiano'];

function CandidateCard({ candidate, onOpen, t }) {
  const avatarInitial = (candidate.display_name || 'C').split(' ')[0][0].toUpperCase();
  return (
    <button
      onClick={() => onOpen(candidate)}
      data-testid={`showcase-card-${candidate.id}`}
      className="group relative text-left bg-white rounded-2xl border border-slate-200 hover:border-slate-900 hover:shadow-lg transition-all overflow-hidden"
    >
      {candidate.featured && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-1 text-xs font-semibold" data-testid={`badge-featured-${candidate.id}`}>
          <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
          {t('talento.featuredBadge')}
        </div>
      )}
      <div
        className="h-24 flex items-end px-5 pb-3 relative"
        style={{ background: seedGradient(candidate.avatar_seed) }}
      >
        <div className="w-16 h-16 rounded-full bg-white text-slate-900 border-4 border-white shadow-md flex items-center justify-center text-2xl font-bold -mb-8">
          {avatarInitial}
        </div>
      </div>
      <div className="p-5 pt-10">
        <h3 className="text-lg font-bold text-slate-900" data-testid={`showcase-name-${candidate.id}`}>
          {candidate.display_name}
        </h3>
        <p className="text-sm font-semibold text-blue-700 mt-0.5">{candidate.headline}</p>
        <p className="text-sm text-slate-600 mt-3 line-clamp-3">
          {candidate.experience_snippet}
        </p>
        <div className="flex flex-wrap gap-2 mt-4 text-xs">
          {candidate.city && (
            <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-2 py-1">
              <MapPin className="h-3 w-3" /> {candidate.city}
            </span>
          )}
          {candidate.experience_years > 0 && (
            <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-2 py-1">
              <Briefcase className="h-3 w-3" /> {t('talento.yearsPlus', { years: candidate.experience_years })}
            </span>
          )}
          {(candidate.languages || []).slice(0, 2).map((l) => (
            <span key={l} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-2 py-1">
              <Languages className="h-3 w-3" /> {l}
            </span>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> {t('talento.protectedContacts')}</span>
          <span className="text-blue-700 font-semibold group-hover:underline">{t('talento.viewProfile')}</span>
        </div>
      </div>
    </button>
  );
}

function DetailModal({ candidate, onClose, isLoggedInEmployer, hasCredits, t }) {
  const navigate = useNavigate();
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!candidate) return;
    setLoading(true);
    showcaseAPI.get(candidate.id)
      .then(r => setFull(r.data))
      .catch(() => setFull({ ...candidate, unlocked: false }))
      .finally(() => setLoading(false));
  }, [candidate]);

  if (!candidate) return null;

  const handleUnlock = async () => {
    try {
      setLoading(true);
      await showcaseAPI.unlock(candidate.id);
      const r = await showcaseAPI.get(candidate.id);
      setFull(r.data);
      toast.success(t('talento.modal.unlockSuccess'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(t('talento.modal.unlockNeedCredits'));
        setTimeout(() => navigate('/checkout'), 800);
      } else {
        toast.error(detail || t('talento.modal.unlockGenericError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (msgSubject.trim().length < 3 || msgBody.trim().length < 10) {
      toast.error(t('talento.modal.messageValidation'));
      return;
    }
    try {
      setSending(true);
      await showcaseAPI.message(candidate.id, { subject: msgSubject, body: msgBody });
      toast.success(t('talento.modal.sendSuccess'));
      setMsgOpen(false);
      setMsgSubject(''); setMsgBody('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('talento.modal.sendError'));
    } finally {
      setSending(false);
    }
  };

  const unlocked = full?.unlocked;
  const avatarInitial = (candidate.display_name || 'C').split(' ')[0][0].toUpperCase();

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="showcase-detail-modal">
          <div className="h-32 relative" style={{ background: seedGradient(candidate.avatar_seed) }}>
            <button onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white bg-black/20 hover:bg-black/40 rounded-full p-1.5" data-testid="close-modal-btn">
              <X className="h-4 w-4" />
            </button>
            <div className="absolute -bottom-10 left-6 w-20 h-20 rounded-full bg-white text-slate-900 border-4 border-white shadow-lg flex items-center justify-center text-3xl font-bold">
              {avatarInitial}
            </div>
          </div>

          <div className="p-6 pt-12">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {unlocked ? full.full_name : candidate.display_name}
                {candidate.featured && <span className="ml-2 text-amber-600 text-sm font-semibold">{t('talento.modal.featuredStar')}</span>}
              </DialogTitle>
              <p className="text-blue-700 font-semibold text-sm">{candidate.headline}</p>
            </DialogHeader>

            {loading && <p className="text-sm text-slate-500 mt-4">{t('talento.modal.loading')}</p>}

            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {candidate.city && (
                <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">
                  <MapPin className="h-3 w-3" /> {candidate.city}
                </span>
              )}
              {candidate.experience_years > 0 && (
                <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">
                  <Briefcase className="h-3 w-3" /> {t('talento.yearsPlus', { years: candidate.experience_years })}
                </span>
              )}
              {(candidate.languages || []).map((l) => (
                <span key={l} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-2.5 py-1">
                  <Languages className="h-3 w-3" /> {l}
                </span>
              ))}
            </div>

            {unlocked ? (
              <div className="mt-6 space-y-5" data-testid="unlocked-content">
                {full.profile_summary && (
                  <section>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{t('talento.modal.about')}</h4>
                    <p className="text-sm text-slate-700 leading-relaxed">{full.profile_summary}</p>
                  </section>
                )}
                {(full.skills || []).length > 0 && (
                  <section>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{t('talento.modal.skills')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {full.skills.map((s) => (
                        <span key={s} className="bg-slate-900 text-white text-xs rounded-md px-2.5 py-1">{s}</span>
                      ))}
                    </div>
                  </section>
                )}
                {(full.work_experience || []).length > 0 && (
                  <section>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{t('talento.modal.experience')}</h4>
                    <ul className="space-y-2">
                      {full.work_experience.map((e, i) => (
                        <li key={i} className="text-sm bg-slate-50 rounded-lg px-3 py-2">
                          <div className="font-semibold text-slate-900">{e.role || e.position || t('talento.modal.defaultRole')}</div>
                          <div className="text-slate-600">{e.company}{e.sector ? ` · ${e.sector}` : ''}</div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {(full.education || []).length > 0 && (
                  <section>
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{t('talento.modal.education')}</h4>
                    <ul className="space-y-1 text-sm">
                      {full.education.map((e, i) => (
                        <li key={i}>
                          <strong>{e.degree || e.course || t('talento.modal.defaultDegree')}</strong>
                          {e.institution ? ` — ${e.institution}` : ''}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                <div className="pt-4 border-t border-slate-100 flex gap-2">
                  <Button
                    onClick={() => setMsgOpen(true)}
                    className="flex-1 bg-blue-700 hover:bg-blue-800"
                    data-testid="btn-send-message"
                  >
                    <Send className="h-4 w-4 mr-2" /> {t('talento.modal.sendMessageBtn')}
                  </Button>
                  {full.cv_url && (
                    <a href={full.cv_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" data-testid="btn-download-cv">{t('talento.modal.fullCvBtn')}</Button>
                    </a>
                  )}
                </div>
                <div className="text-xs text-slate-500 pt-2 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> {t('talento.modal.protectedNote')}
                </div>
              </div>
            ) : (
              <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-6" data-testid="locked-cta">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center">
                    <Lock className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-slate-900">{t('talento.modal.lockedTitle')}</h4>
                    <p className="text-sm text-slate-600">{t('talento.modal.lockedDescFree')}</p>
                  </div>
                </div>
                <div className="mt-5 grid gap-2">
                  {isLoggedInEmployer ? (
                    <Button onClick={handleUnlock} disabled={loading} className="bg-slate-900 hover:bg-black" data-testid="btn-unlock">
                      {loading ? t('talento.modal.loading') : t('talento.modal.unlockFreeBtn')}
                    </Button>
                  ) : (
                    <>
                      <p className="text-xs text-slate-600">{t('talento.modal.registerHint')}</p>
                      <div className="flex gap-2">
                        <Button onClick={() => navigate('/register?role=employer')} className="flex-1 bg-slate-900 hover:bg-black" data-testid="btn-register">
                          {t('talento.modal.registerBtn')}
                        </Button>
                        <Button variant="outline" onClick={() => navigate('/login')} className="flex-1" data-testid="btn-login">
                          {t('talento.modal.loginBtn')}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Message modal */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="max-w-lg" data-testid="message-modal">
          <DialogHeader>
            <DialogTitle>{t('talento.modal.messageTitle', { name: candidate.display_name })}</DialogTitle>
            <p className="text-sm text-slate-500">
              {t('talento.modal.messageDesc')}
            </p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              placeholder={t('talento.modal.subjectPlaceholder')}
              value={msgSubject}
              onChange={(e) => setMsgSubject(e.target.value)}
              maxLength={140}
              data-testid="input-message-subject"
            />
            <Textarea
              placeholder={t('talento.modal.bodyPlaceholder')}
              value={msgBody}
              onChange={(e) => setMsgBody(e.target.value)}
              rows={6}
              maxLength={4000}
              data-testid="input-message-body"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgOpen(false)} data-testid="btn-cancel-message">{t('talento.modal.cancelBtn')}</Button>
            <Button onClick={handleSendMessage} disabled={sending} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-send-message-submit">
              <Send className="h-4 w-4 mr-2" />
              {sending ? t('talento.modal.sending') : t('talento.modal.sendBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Talento() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [city, setCity] = useState('');
  const [language, setLanguage] = useState('all');
  const [selected, setSelected] = useState(null);
  const [me, setMe] = useState(null);

  const { sectors, nameOf } = useSectors();
  const CATEGORIES = sectors.map((c) => ({ slug: c.slug, label: nameOf(c) }));

  useEffect(() => {
    apiClient.get(`/auth/me`)
      .then(r => setMe(r.data))
      .catch(() => setMe(null));
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (category && category !== 'all') params.category = category;
    if (city) params.city = city;
    if (language && language !== 'all') params.language = language;
    showcaseAPI.list(params)
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, language]);

  const isLoggedInEmployer = !!(me && (me.role === 'employer' || me.role === 'admin'));

  return (
    <main className="min-h-screen bg-white text-slate-900" data-testid="talento-page">
      {/* Hero */}
      <section className="relative border-b border-slate-100 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 15% 10%, rgba(29,78,216,0.10), transparent 40%), radial-gradient(circle at 85% 90%, rgba(180,83,9,0.06), transparent 45%)'
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-xs sm:text-[13px] font-semibold tracking-[0.10em] uppercase text-blue-700 mb-4">
            <Sparkles className="h-3.5 w-3.5 inline mr-1" /> {t('talento.pageEyebrow')}
          </p>
          <h1 className="max-w-3xl text-[34px] leading-[1.08] sm:text-[44px] lg:text-[54px] lg:leading-[1.06] font-bold tracking-tighter text-slate-900">
            {t('talento.heroTitle')}
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600 text-base sm:text-lg leading-relaxed">
            <strong className="text-slate-900">{t('talento.heroDescStrong', { count: total > 0 ? total : t('talento.heroDefaultCount') })}</strong>{' '}
            {t('talento.heroDescRest')}
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            <Link
              to="/register?role=employer"
              className="inline-flex items-center gap-2 h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg"
              data-testid="hero-register-btn"
            >
              {t('talento.registerCompanyBtn')}
            </Link>
            <a
              href="#candidates"
              className="inline-flex items-center gap-2 h-11 px-5 bg-white text-slate-900 border border-slate-300 hover:border-slate-900 font-semibold rounded-lg"
            >
              {t('talento.seeCandidatesBtn')}
            </a>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section id="candidates" className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0 sm:min-w-[200px]">
            <Search className="h-4 w-4 text-slate-500" />
            <Input
              placeholder={t('talento.searchCityPlaceholder')}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              className="max-w-xs"
              data-testid="filter-city-input"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[240px]" data-testid="filter-category">
              <SelectValue placeholder={t('talento.allSectors')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('talento.allSectors')}</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-[180px]" data-testid="filter-language">
              <SelectValue placeholder={t('talento.languagePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('talento.anyLanguage')}</SelectItem>
              {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="filter-apply-btn">{t('talento.applyBtn')}</Button>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {loading ? (
          <p className="text-center text-slate-500 py-20">{t('talento.loadingCandidates')}</p>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-lg font-semibold text-slate-900">{t('talento.noneMatch')}</p>
            <p className="text-slate-600 mt-2 text-sm">{t('talento.tryWiderFilters')}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-6" data-testid="results-count">
              {t('talento.showingCount', { shown: items.length, total })}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="candidates-grid">
              {items.map(c => (
                <CandidateCard key={c.id} candidate={c} onOpen={setSelected} t={t} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Privacy note */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">{t('talento.privacyTitle')}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                {t('talento.privacyBody')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {selected && (
        <DetailModal
          candidate={selected}
          onClose={() => setSelected(null)}
          isLoggedInEmployer={isLoggedInEmployer}
          hasCredits={true}
          t={t}
        />
      )}
    </main>
  );
}
